import type {
  AppBlueprint,
  BlueprintBlock,
  BlueprintForm,
  BlueprintPage,
  BlueprintSetting,
  BlueprintTable,
} from '@/pages/superadmin/apps/builder/blueprint';

// ── Composeur de blueprint pour le chat du constructeur ────────────────
// Ce moteur transforme une demande en langage naturel en une structure
// d'application (pages, blocs, formulaires, tables, réglages) en respectant
// les conventions Zifek. Il fonctionne sans service externe : la conversation
// reste donc utilisable même si aucune clé IA n'est configurée.

export interface ComposeContext {
  appName: string;
  pageTitles: string[];
  history: { role: 'user' | 'assistant'; content: string }[];
}

export interface ComposeResult {
  reply: string;
  blueprint: AppBlueprint;
  removePageSlugs: string[];
  analysis: string[];
}

interface DomainSpec {
  slug: string;
  keys: string[];
  page: { title: string; slug: string; icon: string };
  intro: string;
  table: BlueprintTable;
  form: BlueprintForm;
  setting?: BlueprintSetting;
}

const DOMAINS: DomainSpec[] = [
  {
    slug: 'taches',
    keys: ['tâche', 'tache', 'todo', 'devoir', 'planning', 'agenda', 'rappel'],
    page: { title: 'Tâches', slug: 'taches', icon: 'ri-task-line' },
    intro: 'Suivez vos tâches, leurs priorités et leurs échéances.',
    table: {
      name: 'Tâches',
      slug: 'taches',
      fields: [
        { label: 'Titre', type: 'text' },
        { label: 'Priorité', type: 'select' },
        { label: 'Échéance', type: 'date' },
        { label: 'Statut', type: 'select' },
      ],
      rows: [
        { Titre: 'Préparer la réunion', Priorité: 'Haute', Échéance: '2026-11-04', Statut: 'À faire' },
        { Titre: 'Mettre à jour le suivi', Priorité: 'Normale', Échéance: '2026-11-07', Statut: 'En cours' },
        { Titre: 'Archiver les documents', Priorité: 'Basse', Échéance: '2026-11-12', Statut: 'Terminé' },
      ],
    },
    form: {
      name: 'Nouvelle tâche',
      title: 'Ajouter une tâche',
      description: 'Décrivez la tâche, sa priorité et son échéance.',
      fields: [
        { label: 'Titre', type: 'text', required: true },
        { label: 'Description', type: 'textarea' },
        { label: 'Priorité', type: 'select', options: ['Basse', 'Normale', 'Haute'] },
        { label: 'Échéance', type: 'date' },
      ],
    },
    setting: { key: 'rappels', label: 'Activer les rappels', type: 'toggle', value: '1' },
  },
  {
    slug: 'notes',
    keys: ['note', 'carnet', 'cours', 'matière', 'matiere', 'révision', 'revision'],
    page: { title: 'Notes', slug: 'notes', icon: 'ri-sticky-note-line' },
    intro: 'Centralisez vos notes par matière et par chapitre.',
    table: {
      name: 'Notes',
      slug: 'notes',
      fields: [
        { label: 'Titre', type: 'text' },
        { label: 'Matière', type: 'text' },
        { label: 'Chapitre', type: 'text' },
        { label: 'À réviser', type: 'boolean' },
      ],
      rows: [
        { Titre: 'Dérivées et tangentes', Matière: 'Mathématiques', Chapitre: 'Chapitre 2', 'À réviser': 'Oui' },
        { Titre: 'Lois de Newton', Matière: 'Physique', Chapitre: 'Chapitre 1', 'À réviser': 'Non' },
      ],
    },
    form: {
      name: 'Nouvelle note',
      title: 'Créer une note',
      description: 'Titre, matière et contenu de la note.',
      fields: [
        { label: 'Titre', type: 'text', required: true },
        { label: 'Matière', type: 'text' },
        { label: 'Contenu', type: 'textarea' },
      ],
    },
  },
  {
    slug: 'depenses',
    keys: ['dépense', 'depense', 'budget', 'comptabilité', 'comptabilite', 'argent', 'facture'],
    page: { title: 'Dépenses', slug: 'depenses', icon: 'ri-wallet-3-line' },
    intro: 'Enregistrez vos dépenses et suivez votre budget.',
    table: {
      name: 'Dépenses',
      slug: 'depenses',
      fields: [
        { label: 'Date', type: 'date' },
        { label: 'Catégorie', type: 'select' },
        { label: 'Montant', type: 'number' },
        { label: 'Moyen de paiement', type: 'select' },
      ],
      rows: [
        { Date: '2026-11-02', Catégorie: 'Fournitures', Montant: '240', 'Moyen de paiement': 'Espèces' },
        { Date: '2026-11-05', Catégorie: 'Transport', Montant: '90', 'Moyen de paiement': 'Carte' },
      ],
    },
    form: {
      name: 'Nouvelle dépense',
      title: 'Ajouter une dépense',
      fields: [
        { label: 'Date', type: 'date', required: true },
        { label: 'Catégorie', type: 'select', options: ['Fournitures', 'Transport', 'Loyer', 'Autre'] },
        { label: 'Montant', type: 'number', required: true },
        { label: 'Note', type: 'textarea' },
      ],
    },
    setting: { key: 'devise', label: 'Devise affichée', type: 'select', value: 'MAD', options: ['MAD', 'EUR', 'USD'] },
  },
  {
    slug: 'contacts',
    keys: ['contact', 'client', 'carnet d’adresse', 'annuaire', 'prospect'],
    page: { title: 'Contacts', slug: 'contacts', icon: 'ri-contacts-line' },
    intro: 'Gardez vos contacts et prospects organisés.',
    table: {
      name: 'Contacts',
      slug: 'contacts',
      fields: [
        { label: 'Nom', type: 'text' },
        { label: 'Email', type: 'text' },
        { label: 'Téléphone', type: 'text' },
        { label: 'Statut', type: 'select' },
      ],
      rows: [
        { Nom: 'Salma Bennani', Email: 'salma@exemple.ma', Téléphone: '+212 6 12 34 56 78', Statut: 'Client' },
        { Nom: 'Youssef Alami', Email: 'y.alami@exemple.ma', Téléphone: '+212 6 98 76 54 32', Statut: 'Prospect' },
      ],
    },
    form: {
      name: 'Nouveau contact',
      title: 'Ajouter un contact',
      fields: [
        { label: 'Nom', type: 'text', required: true },
        { label: 'Email', type: 'email' },
        { label: 'Téléphone', type: 'tel' },
        { label: 'Notes', type: 'textarea' },
      ],
    },
  },
  {
    slug: 'reservations',
    keys: ['réservation', 'reservation', 'rendez-vous', 'rdv', 'booking', 'créneau', 'creneau'],
    page: { title: 'Réservations', slug: 'reservations', icon: 'ri-calendar-check-line' },
    intro: 'Gérez les demandes de réservation et les créneaux.',
    table: {
      name: 'Réservations',
      slug: 'reservations',
      fields: [
        { label: 'Client', type: 'text' },
        { label: 'Service', type: 'text' },
        { label: 'Date', type: 'date' },
        { label: 'Statut', type: 'select' },
      ],
      rows: [
        { Client: 'Nadia R.', Service: 'Consultation', Date: '2026-11-08', Statut: 'Confirmé' },
        { Client: 'Karim B.', Service: 'Atelier', Date: '2026-11-09', Statut: 'En attente' },
      ],
    },
    form: {
      name: 'Nouvelle réservation',
      title: 'Demander une réservation',
      fields: [
        { label: 'Nom', type: 'text', required: true },
        { label: 'Email', type: 'email', required: true },
        { label: 'Service', type: 'select', options: ['Consultation', 'Atelier', 'Autre'] },
        { label: 'Date souhaitée', type: 'date', required: true },
        { label: 'Message', type: 'textarea' },
      ],
    },
  },
  {
    slug: 'catalogue',
    keys: ['produit', 'catalogue', 'menu', 'article', 'boutique', 'vente'],
    page: { title: 'Catalogue', slug: 'catalogue', icon: 'ri-store-3-line' },
    intro: 'Présentez vos produits ou vos prestations.',
    table: {
      name: 'Catalogue',
      slug: 'catalogue',
      fields: [
        { label: 'Produit', type: 'text' },
        { label: 'Catégorie', type: 'text' },
        { label: 'Prix', type: 'number' },
        { label: 'Disponible', type: 'boolean' },
      ],
      rows: [
        { Produit: 'Formule découverte', Catégorie: 'Offres', Prix: '190', Disponible: 'Oui' },
        { Produit: 'Formule complète', Catégorie: 'Offres', Prix: '390', Disponible: 'Oui' },
      ],
    },
    form: {
      name: 'Nouveau produit',
      title: 'Ajouter un produit',
      fields: [
        { label: 'Nom', type: 'text', required: true },
        { label: 'Catégorie', type: 'text' },
        { label: 'Prix', type: 'number' },
        { label: 'Description', type: 'textarea' },
      ],
    },
  },
  {
    slug: 'evenements',
    keys: ['événement', 'evenement', 'event', 'atelier', 'conférence', 'conference'],
    page: { title: 'Événements', slug: 'evenements', icon: 'ri-calendar-event-line' },
    intro: 'Annoncez vos événements et gérez les inscriptions.',
    table: {
      name: 'Événements',
      slug: 'evenements',
      fields: [
        { label: 'Titre', type: 'text' },
        { label: 'Date', type: 'date' },
        { label: 'Lieu', type: 'text' },
        { label: 'Places', type: 'number' },
      ],
      rows: [
        { Titre: 'Atelier découverte', Date: '2026-11-15', Lieu: 'Centre-ville', Places: '25' },
        { Titre: 'Rencontre mensuelle', Date: '2026-11-28', Lieu: 'En ligne', Places: '100' },
      ],
    },
    form: {
      name: 'Inscription événement',
      title: 'S’inscrire à un événement',
      fields: [
        { label: 'Nom', type: 'text', required: true },
        { label: 'Email', type: 'email', required: true },
        { label: 'Nombre de places', type: 'number' },
      ],
    },
  },
  {
    slug: 'projets',
    keys: ['projet', 'project', 'tâches d’équipe', 'kanban', 'suivi'],
    page: { title: 'Projets', slug: 'projets', icon: 'ri-projector-line' },
    intro: 'Suivez vos projets et leur avancement.',
    table: {
      name: 'Projets',
      slug: 'projets',
      fields: [
        { label: 'Projet', type: 'text' },
        { label: 'Responsable', type: 'text' },
        { label: 'Échéance', type: 'date' },
        { label: 'Avancement', type: 'select' },
      ],
      rows: [
        { Projet: 'Refonte du site', Responsable: 'Équipe design', Échéance: '2026-12-01', Avancement: 'En cours' },
        { Projet: 'Lancement offre', Responsable: 'Marketing', Échéance: '2026-12-15', Avancement: 'À faire' },
      ],
    },
    form: {
      name: 'Nouveau projet',
      title: 'Créer un projet',
      fields: [
        { label: 'Nom du projet', type: 'text', required: true },
        { label: 'Responsable', type: 'text' },
        { label: 'Échéance', type: 'date' },
        { label: 'Description', type: 'textarea' },
      ],
    },
  },
];

const MALICIOUS_PATTERNS = [
  /vol(er|e)?\s+(les\s+)?(mot de passe|identifiants|cookies?)/i,
  /keylogger/i,
  /phishing/i,
  /carte\s+bancaire\s+des\s+visiteurs/i,
  /script\s+malveillant/i,
  /injecter?\s+du\s+javascript/i,
];

function detectDomains(text: string): DomainSpec[] {
  const lower = text.toLowerCase();
  const found = DOMAINS.filter((domain) => domain.keys.some((key) => lower.includes(key)));
  return found.length > 0 ? found : [DOMAINS[0]];
}

function blocksForDomain(domain: DomainSpec): BlueprintBlock[] {
  return [
    { type: 'heading', content: { text: domain.page.title } },
    { type: 'text', content: { text: domain.intro } },
    { type: 'list', content: { title: 'Ce que cette page permet', items: [
      'Consulter les éléments existants',
      'Ajouter un nouvel élément via le formulaire',
      'Suivre les mises à jour depuis le tableau',
    ] } },
    { type: 'form', content: { form: domain.form.name } },
    { type: 'table', content: { table: domain.table.slug } },
  ];
}

function homePage(appName: string, domains: DomainSpec[]): BlueprintPage {
  return {
    title: 'Accueil',
    slug: 'accueil',
    icon: 'ri-home-5-line',
    blocks: [
      { type: 'heading', content: { text: appName } },
      {
        type: 'text',
        content: {
          text: `Bienvenue dans ${appName}. Cette application regroupe les outils essentiels pour travailler au quotidien.`,
        },
      },
      {
        type: 'list',
        content: {
          title: 'Modules disponibles',
          items: domains.map((domain) => `${domain.page.title} — ${domain.intro}`),
        },
      },
      { type: 'spacer', content: { height: 24 } },
    ],
  };
}

function cleanPageTitle(raw: string): string {
  const cleaned = raw
    .replace(/^(une|la|le|des|les)\s+/i, '')
    .replace(/\s*(avec|qui|pour|contenant|comprenant|et)\s.*$/i, '')
    .trim();
  return cleaned.length > 0 ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : 'Nouvelle page';
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'page';

export function composeBlueprint(prompt: string, context: ComposeContext): ComposeResult {
  const trimmed = prompt.trim();
  const analysis: string[] = [];

  if (MALICIOUS_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return {
      reply:
        "Je ne peux pas construire une fonctionnalité qui collecte des identifiants, des mots de passe ou des données bancaires sans consentement. Si votre besoin est légitime (par exemple un formulaire de contact), reformulez-le : je le construirai avec les protections Zifek.",
      blueprint: { pages: [] },
      removePageSlugs: [],
      analysis: ['Demande refusée : contenu contraire aux règles de sécurité.'],
    };
  }

  // Suppression d'une page : « supprime la page contact »
  const removeMatch = trimmed.match(/(supprime|retire|enl[eè]ve)\s+(la\s+)?page\s+(.+)/i);
  if (removeMatch) {
    const target = removeMatch[3].trim().toLowerCase();
    const page = context.pageTitles.find((title) => title.toLowerCase().includes(target));
    if (!page) {
      return {
        reply: `Je ne trouve pas de page correspondant à « ${target} ». Pages existantes : ${context.pageTitles.join(', ') || 'aucune'}.`,
        blueprint: { pages: [] },
        removePageSlugs: [],
        analysis: [],
      };
    }
    return {
      reply: `La page « ${page} » a été retirée du brouillon. Vous pouvez annuler depuis l'onglet Versions si nécessaire.`,
      blueprint: { pages: [] },
      removePageSlugs: [slugify(page)],
      analysis: [`Suppression demandée : ${page}`],
    };
  }

  // Ajout d'une page précise : « ajoute une page qui gère les dépenses »
  const pageMatch = trimmed.match(/(ajoute|cr[ée]e|construis|g[ée]n[èe]re)\s+(une\s+)?page\s+(?:qui\s+)?(?:permet\s+de\s+)?(.+)/i);
  const isAddPage = Boolean(pageMatch);

  const domains = detectDomains(trimmed);
  analysis.push(`Domaines détectés : ${domains.map((domain) => domain.page.title).join(', ')}`);

  if (isAddPage && pageMatch) {
    const rawTitle = pageMatch[4];
    const domain = domains[0];
    const title = cleanPageTitle(rawTitle) || domain.page.title;
    const slug = slugify(title);
    const existing = context.pageTitles.map((item) => item.toLowerCase());
    if (existing.includes(title.toLowerCase())) {
      return {
        reply: `La page « ${title} » existe déjà. Je peux y ajouter des blocs ou changer son contenu : précisez ce que vous voulez y mettre.`,
        blueprint: { pages: [] },
        removePageSlugs: [],
        analysis,
      };
    }

    const page: BlueprintPage = {
      title,
      slug,
      icon: domain.page.icon,
      blocks: [
        { type: 'heading', content: { text: title } },
        { type: 'text', content: { text: `Page dédiée à ${title.toLowerCase()}. ${domain.intro}` } },
        ...blocksForDomain(domain).slice(1),
      ],
    };

    const usedFormName = `${title} — formulaire`;
    page.blocks = page.blocks.map((block) =>
      block.type === 'form' ? { ...block, content: { form: usedFormName } } : block,
    );

    return {
      reply: `C'est fait : la page « ${title} » est ajoutée au brouillon, avec un formulaire (${domain.form.fields.map((f) => f.label).join(', ')}) et un tableau de suivi. Dites-moi si vous voulez un autre champ, un bouton ou une image.`,
      blueprint: {
        pages: [page],
        forms: [{ ...domain.form, name: usedFormName, title }],
        tables: [{ ...domain.table, name: `${title} — données`, slug: `${slug}-donnees` }],
      },
      removePageSlugs: [],
      analysis,
    };
  }

  // Génération complète : première demande ou refonte du contenu.
  const pages: BlueprintPage[] = [homePage(context.appName, domains)];
  const forms: BlueprintForm[] = [];
  const tables: BlueprintTable[] = [];
  const settings: BlueprintSetting[] = [];

  domains.forEach((domain, index) => {
    if (index > 0) {
      pages.push({
        title: domain.page.title,
        slug: domain.page.slug,
        icon: domain.page.icon,
        blocks: blocksForDomain(domain),
      });
      forms.push(domain.form);
      tables.push(domain.table);
    } else {
      pages.push({
        title: domain.page.title,
        slug: domain.page.slug,
        icon: domain.page.icon,
        blocks: blocksForDomain(domain),
      });
      forms.push(domain.form);
      tables.push(domain.table);
    }
    if (domain.setting) settings.push(domain.setting);
  });

  pages.push({
    title: 'À propos',
    slug: 'a-propos',
    icon: 'ri-information-line',
    blocks: [
      { type: 'heading', content: { text: 'À propos' } },
      {
        type: 'text',
        content: {
          text: `${context.appName} est une application Zifek. Toutes les données sont stockées dans le Backend Zifek et accessibles uniquement aux personnes autorisées.`,
        },
      },
      { type: 'list', content: { title: 'Bonnes pratiques', items: [
        'Ne partagez jamais vos identifiants',
        'Vérifiez les champs obligatoires avant d’enregistrer',
        'Signalez toute anomalie depuis le support Zifek',
      ] } },
    ],
  });

  const moduleList = domains.map((domain) => domain.page.title.toLowerCase()).join(', ');
  return {
    reply: `J'ai composé la structure de ${context.appName} : une page d'accueil, ${domains.length} module(s) (${moduleList}) et une page À propos. Chaque module contient un formulaire de saisie et un tableau de suivi. Vous pouvez maintenant me demander d'ajouter une page, un champ ou un bouton, ou soumettre l'app à validation.`,
    blueprint: { pages, forms, tables, settings },
    removePageSlugs: [],
    analysis,
  };
}